import { useSidebarPinState } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MobileTabBar, MOBILE_TAB_BAR_HEIGHT } from '../nav/MobileTabBar';
import { MobileTopBar } from './MobileTopBar';
import { PortalHeader } from './PortalHeader';
import { routeTitle } from './routeTitle';

const useStyles = makeStyles({
  // Desktop: a full-height column, breadcrumb bar above a scrolling content area.
  desktopColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  // The routed content scrolls inside the column, not the document, so the
  // TechDocs reader's uncapped container does not drag the chrome with it.
  desktopContent: { flex: 1, minHeight: 0, overflowY: 'auto' },
  // Mobile: this is the sole scroll container — it holds the top app bar and the
  // content, and the fixed bottom tab bar sits over it, so the content reserves
  // its height. The routed page (vendor `main[data-backstage-core-page]`) keeps
  // its own `overflow-y: auto` at the phone breakpoint; hone-portal.css collapses
  // that so it flows into this scroller rather than nesting a second one.
  // `overscroll-behavior: contain` keeps a bounce at the ends from chaining to
  // the document.
  mobileScroll: {
    height: '100vh',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    background: 'var(--bui-bg-neutral-2)',
  },
  mobileContent: {
    paddingBottom: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
  },
});

/*
 * The announcements list, detail and admin pages (`@backstage-community/plugin-announcements`)
 * are the only routed surface built on Backstage UI's `Header`/`Card` rather than
 * MUI (see the "Announcements" section of `hone-portal.css`), and the plugin
 * renders its `HeaderPage` and `Card`s as siblings with no wrapping element of
 * its own to hang a scoping selector off. `data-hone-scope` fills that gap here,
 * on the one element that already wraps every route's content, so those rules
 * can target `[data-hone-scope='announcements']` instead of the bare BUI class
 * names, which would otherwise also match other routed pages' BUI `Header`s and
 * `Card`s (feedback, admin settings, Slack channels).
 */
function useRouteScope(pathname: string): string | undefined {
  const [rootSegment] = pathname.split('/').filter(Boolean);
  return rootSegment === 'announcements' ? rootSegment : undefined;
}

/*
 * A page's Helmet leaves its title in the tab when it unmounts, so a route with
 * no Helmet of its own (the Documentation landing, the catalog index) kept the
 * last entity's name. Set the tab title from the route on every navigation;
 * pages that set their own title run after this and replace it.
 */
function useRouteDocumentTitle(pathname: string) {
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Hone Portal';
  useEffect(() => {
    const title = routeTitle(pathname);
    if (title) document.title = `${title} | ${appTitle}`;
  }, [pathname, appTitle]);
}

// Chooses the desktop or mobile chrome around the routed content. Runs inside
// `SidebarPage`, so `useSidebarPinState` reports the same phone breakpoint
// (`down('xs')`) that switches Backstage's own sidebar to its mobile form.
export function PortalShell({ children }: { children: ReactNode }) {
  const classes = useStyles();
  const { isMobile } = useSidebarPinState();
  const { pathname } = useLocation();
  const scope = useRouteScope(pathname);
  useRouteDocumentTitle(pathname);

  if (isMobile) {
    return (
      <>
        <div className={classes.mobileScroll}>
          <MobileTopBar />
          <div className={classes.mobileContent} data-hone-scope={scope}>
            {children}
          </div>
        </div>
        <MobileTabBar />
      </>
    );
  }

  return (
    <div className={classes.desktopColumn}>
      <PortalHeader />
      <div className={classes.desktopContent} data-hone-scope={scope}>
        {children}
      </div>
    </div>
  );
}
